import {useNavigate} from 'react-router-dom';
import Cookies from 'js-cookie';
import {COOKIES} from '../../utils/cookie-names';
import {ROUTES} from '../../utils/ROUTES';
import {doesNotHaveTwoFactorEnabledAndIsEmailAuth, hasBeenPromptedForJobPreferences, hasCreatedProfile, isEmployer, isJobSeeker, user_type} from '../../utils/variables/userVariables';
import {Modal} from 'antd';

/**
 * Custom hook to handle authentication redirects
 * Extracts the redirect logic from Login component
 */
export const useAuthRedirect = () => {
	const navigate = useNavigate();

	const handleRedirect = (currentUser) => {
		const TWO_FACTOR_ASKED_STRING = 'twoFactorAsked';
		if (!localStorage.getItem(TWO_FACTOR_ASKED_STRING) && doesNotHaveTwoFactorEnabledAndIsEmailAuth(currentUser)) {
			Modal.confirm({
				title: '2FA Security Recommendation',
				content: 'We recommend setting up two-factor authentication for your account. This will enhance your account security and protect your data.',
				onOk: () => {
					window.open(ROUTES.SETUP_TWO_FACTOR, '_blank');
				},
				okText: 'Take me to setup',
				cancelText: `I'll do it later`,
				closable: true,
				destroyOnClose: true,
				maskProps: {
					style: {
						backdropFilter: 'blur(4px)',
					},
				},
			});
			localStorage.setItem(TWO_FACTOR_ASKED_STRING, 'true');
		}

		// Handle job application redirect from single job page
		if (Cookies.get(COOKIES.apply_job_redirect) && Cookies.get(COOKIES.is_single_job_page)) {
			const url = Cookies.get(COOKIES.apply_job_redirect);
			navigate(url);
			Cookies.remove(COOKIES.apply_job_redirect);
			Cookies.remove(COOKIES.is_single_job_page);
			return;
		}

		// Handle general job application redirect
		if (Cookies.get(COOKIES.apply_job_redirect)) {
			navigate(Cookies.get(COOKIES.apply_job_redirect));
			Cookies.remove(COOKIES.apply_job_redirect);
			return;
		}

		if (!hasCreatedProfile(currentUser) && Cookies.get(COOKIES.post_job_redirect)) {
			navigate(ROUTES.MY_PROFILE);
			return;
		}

		// Handle post job redirect
		if (Cookies.get(COOKIES.post_job_redirect)) {
			const redirectUrl = Cookies.get(COOKIES.post_job_redirect);
			navigate(redirectUrl);
			Cookies.remove(COOKIES.post_job_redirect);
			return;
		}

		// Handle unauthorized redirect
		if (Cookies.get(COOKIES.unauthorized_redirect)) {
			navigate(Cookies.get(COOKIES.unauthorized_redirect));
			Cookies.remove(COOKIES.unauthorized_redirect);
			return;
		}

		// Handle redirect to preferences
		if (!hasBeenPromptedForJobPreferences(currentUser) && isJobSeeker(currentUser)) {
			navigate(ROUTES.JOB_PREFERENCE);
			return;
		}

		navigate('/');
	};

	const handleProfessionalAccountSubscriptionRedirect = () => {
		if (Cookies.get(COOKIES.post_job_redirect)) {
			navigate(ROUTES.POST_JOB);
			Cookies.remove(COOKIES.post_job_redirect);
			return;
		} else {
			navigate(ROUTES.DASHBOARD);
			Modal.success({
				title: 'Successfully upgraded to Professional Account',
				content: 'You now have access to post unlimited premium jobs. Get started by posting your first job.',
				okText: 'Post Job',
				closable: true,
				onOk: () => {
					navigate(ROUTES.POST_JOB);
				},
				destroyOnClose: true,
			});
			return;
		}
	};

	const handleFreeAccountSubscriptionRedirect = () => {
		if (Cookies.get(COOKIES.post_job_redirect)) {
			navigate(ROUTES.POST_JOB);
			Cookies.remove(COOKIES.post_job_redirect);
			return;
		} else {
			navigate(ROUTES.POST_JOB);
			return;
		}
	};

	const handleCreateProfileRedirect = (userTypeFromForm) => {
		if (userTypeFromForm === user_type.EMPLOYER) {
			navigate(ROUTES.ACCOUNT_SUBSCRIPTION);
		} else if (Cookies.get(COOKIES.apply_job_redirect)) {
			const redirectUrl = Cookies.get(COOKIES.apply_job_redirect);
			Cookies.remove(COOKIES.apply_job_redirect);
			navigate(redirectUrl);
		} else {
			if (userTypeFromForm === user_type.JOB_SEEKER) {
				navigate(ROUTES.JOB_PREFERENCE);
			} else {
				window.location.reload();
			}
		}
	};

	const handleJobPreferenceRedirect = (notRedirect = false) => {
		if (notRedirect) {
			return;
		}

		navigate(ROUTES.HOME);
	};

	return {handleRedirect, handleProfessionalAccountSubscriptionRedirect, handleCreateProfileRedirect, handleFreeAccountSubscriptionRedirect, handleJobPreferenceRedirect};
};
